import { FC, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import type { PDFPageProxy } from 'pdfjs-dist';

interface PdfPageRendererProps {
  page: PDFPageProxy;
  scale: number;
  pageIndex: number;
  children?: ReactNode;
}

const PdfPageRenderer: FC<PdfPageRendererProps> = ({
  page,
  scale,
  pageIndex,
  children,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<ReturnType<PDFPageProxy['render']> | null>(null);

  const renderPage = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
      renderTaskRef.current = null;
    }

    const viewport = page.getViewport({ scale });
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    const outputScale = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.floor(viewport.width * outputScale);
    canvas.height = Math.floor(viewport.height * outputScale);
    canvas.style.width = `${Math.floor(viewport.width)}px`;
    canvas.style.height = `${Math.floor(viewport.height)}px`;

    const transform =
      outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined;

    try {
      const task = page.render({
        canvasContext: ctx,
        viewport,
        transform,
        canvas,
      });
      renderTaskRef.current = task;
      await task.promise;
    } catch {
      // RenderingCancelledException is expected when scale changes mid-render
    }
  }, [page, scale]);

  useEffect(() => {
    renderPage();
    return () => {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [renderPage]);

  const viewport = useMemo(() => page.getViewport({ scale }), [page, scale]);

  return (
    <Box
      data-page-index={pageIndex}
      sx={{
        position: 'relative',
        width: Math.floor(viewport.width),
        height: Math.floor(viewport.height),
        mx: 'auto',
        mb: 0.5,
        boxShadow: 1,
        backgroundColor: '#fff',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{ display: 'block', width: '100%', height: '100%' }}
        role="img"
        aria-label={`PDF page ${pageIndex + 1}`}
      />
      {children}
    </Box>
  );
};

export default PdfPageRenderer;
